import useRequest from "@/lib/request/useRequest";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { object, date, number, string, InferType } from "yup";
import PlantingCropDetailsForm from "../field/operations/PlantingCropDetailsForm";
import { CropConfiguration } from "@/types/cropConfiguration";
import {
    Button,
    ModalContent,
    ModalBody,
    ModalHeader,
    Modal,
    ModalFooter,
    Spinner,
} from "@heroui/react";
import { FieldConfig } from "@/types/fieldConfig";
import { PlantingOperation } from "@/types/operations";
import NumberInputComponent from "../inputs/NumberInput";
import dayjs from "dayjs";
import { roundTo } from "@/lib/utils/numbers";
import { useTranslation } from "react-i18next";

const schema = object().shape({
    start_date: date().required().label("Start Date"),
    crop_configuration: number().required().label("Crop"),
    crm: number()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" ? null : value,
        )
        .integer(),
    gdd_silk: number()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" ? null : value,
        )
        .integer(),
    gdd_mature: number()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" ? null : value,
        )
        .integer(),
    hybrid: string().nullable(),
    maturity_level: string().nullable().oneOf(["early", "medium", "late"]),
    rice_type: string().nullable().oneOf(["american", "japanese"]),
    sugarcane_type: string().nullable().oneOf(["high_fiber", "high_sucrose"]),
    soybean_maturity_group: number().nullable().min(1).max(11),
    seeding_rate: number().nullable().min(0),
    planting_depth: number().nullable().min(0),
});

type FormType = InferType<typeof schema>;

function BulkPlantingOperationForm({
    fields,
    onFinish,
}: {
    fields: FieldConfig[];
    onFinish: () => void;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();
    const { t } = useTranslation();

    const isSingleEdit = useMemo(() => {
        return fields.length === 1;
    }, [fields]);
    const existingPlantingOperationId =
        isSingleEdit &&
        fields[0].active_planting_operation.id !== -1
            ? fields[0].active_planting_operation.id
            : undefined;
    const existingPlantingOperationQuery = useGetRequest<PlantingOperation>({
        url: `planting_operations/${existingPlantingOperationId}/`,
        enabled: !!existingPlantingOperationId,
    });
    const [initializedPlantingOperationId, setInitializedPlantingOperationId] =
        useState<number>();
    const isInitialized =
        !existingPlantingOperationId ||
        initializedPlantingOperationId === existingPlantingOperationId;

    const singlePlantingOperationQuery = useGetRequest<
        { id: number; mean: number; mean_1: number; mean_2: number }[]
    >({
        url: `planting_operations/${fields[0].active_planting_operation.id}/rates/`,
        params: {
            by: "field",
        },
        enabled: isSingleEdit && fields[0].active_planting_operation.id !== -1,
    });

    const cropsQuery = useGetRequest<CropConfiguration[]>({
        url: "crops/",
    });

    const f = useForm<FormType>({
        resolver: yupResolver(schema),
    });

    useEffect(() => {
        if (
            !isInitialized &&
            !existingPlantingOperationQuery.isFetching &&
            existingPlantingOperationQuery.isSuccess &&
            existingPlantingOperationQuery.data
        ) {
            const planting = existingPlantingOperationQuery.data;
            f.setValue("start_date", dayjs(planting.start_date).toDate());
            f.setValue(
                "crop_configuration",
                // @ts-expect-error - id is a number
                planting.crop_configuration.id.toString() as number,
            );
            f.setValue("hybrid", planting.varieties[0]?.name);
            f.setValue("crm", planting.varieties[0]?.crm);
            f.setValue("gdd_silk", planting.varieties[0]?.gdd_silk);
            f.setValue("gdd_mature", planting.varieties[0]?.gdd_mature);
            f.setValue("maturity_level", planting.varieties[0]?.maturity_level);
            f.setValue("rice_type", planting.varieties[0]?.rice_type);
            f.setValue("sugarcane_type", planting.varieties[0]?.sugarcane_type);
            f.setValue(
                "soybean_maturity_group",
                planting.varieties[0]?.soybean_maturity_group,
            );
            setInitializedPlantingOperationId(existingPlantingOperationId);
        }
    }, [
        isInitialized,
        existingPlantingOperationId,
        existingPlantingOperationQuery.data,
        existingPlantingOperationQuery.isFetching,
        existingPlantingOperationQuery.isSuccess,
        f,
    ]);

    useEffect(() => {
        if (singlePlantingOperationQuery.data) {
            const fieldRate = singlePlantingOperationQuery.data[0];

            if (fieldRate.mean !== null && fieldRate.mean !== undefined) {
                f.setValue("seeding_rate", roundTo(fieldRate.mean, 2));
            } else {
                f.setValue("seeding_rate", roundTo(fieldRate.mean_1, 2));
                f.setValue("planting_depth", roundTo(fieldRate.mean_2, 2));
            }
        }
    }, [f, singlePlantingOperationQuery.data]);

    const [crop_configuration] = f.watch(["crop_configuration"]);

    const selectedCrop = useMemo(() => {
        return cropsQuery.data?.find((crop) => crop.id == crop_configuration);
    }, [cropsQuery.data, crop_configuration]);

    const bulkUpsertPlantingOperation = useMutateRequest({
        url: "planting_operations/bulk_upsert/",
        method: "put",
        successMessage: "Planting information updated successfully",
        errorMessage: "Failed to update planting information",
    });

    const onSubmit = useCallback(
        (data: FormType) => {
            if (!isInitialized || !selectedCrop) return;

            bulkUpsertPlantingOperation.mutate(
                {
                    data: fields.map((field) => {
                        return {
                            id:
                                field.active_planting_operation.id === -1
                                    ? null
                                    : field.active_planting_operation.id,
                            start_date: dayjs(data.start_date).format(
                                "YYYY-MM-DD",
                            ),
                            field: field.id,
                            crop_configuration: data.crop_configuration,
                            varieties: [
                                {
                                    name: data.hybrid,
                                    grower: field.grower,
                                    ...(selectedCrop.planting_form_fields.includes(
                                        "crm",
                                    )
                                        ? {
                                              crm: data.crm,
                                              gdd_silk: data.gdd_silk,
                                              gdd_mature: data.gdd_mature,
                                          }
                                        : {}),
                                    maturity_level: data.maturity_level,
                                    rice_type: data.rice_type,
                                    sugarcane_type: data.sugarcane_type,
                                    soybean_maturity_group:
                                        data.soybean_maturity_group,
                                },
                            ],
                            field_rate: {
                                rate_1: data.seeding_rate ?? 0,
                                rate_2: data.planting_depth ?? 0,
                            },
                        };
                    }),
                },
                {
                    onSuccess: () => {
                        onFinish();
                    },
                },
            );
        },
        [
            bulkUpsertPlantingOperation,
            fields,
            onFinish,
            isInitialized,
            selectedCrop,
        ],
    );

    const editingExistingOperations = useMemo(() => {
        return fields.some(
            (field) => field.active_planting_operation.id !== -1,
        );
    }, [fields]);

    return (
        <Modal isOpen size="xl" onClose={onFinish}>
            <ModalContent>
                <FormProvider {...f}>
                    <form>
                        <ModalHeader>Set Crop/Planting Date</ModalHeader>
                        <ModalBody>
                            {!isInitialized &&
                                (existingPlantingOperationQuery.isError ? (
                                    <div className="text-sm text-red-500">
                                        Unable to load planting details.
                                    </div>
                                ) : (
                                    <Spinner />
                                ))}
                            {isInitialized && (
                                <div className="flex flex-col gap-2">
                                    <PlantingCropDetailsForm
                                        crops={
                                            cropsQuery.data?.map((crop) => ({
                                                id: crop.id,
                                                description: crop.crop_name,
                                            })) ?? []
                                        }
                                        selectedCrop={selectedCrop}
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        <NumberInputComponent
                                            name="seeding_rate"
                                            label={
                                                t("seedingRate", {
                                                    ns: "operations",
                                                }) +
                                                " (" +
                                                t("seedingRateUnit", {
                                                    ns: "operations",
                                                }) +
                                                ")"
                                            }
                                            labelPlacement="outside"
                                            size="sm"
                                            placeholder=" "
                                            isReadOnly={
                                                singlePlantingOperationQuery.isLoading
                                            }
                                            isDisabled={
                                                singlePlantingOperationQuery.isLoading
                                            }
                                        />
                                        <NumberInputComponent
                                            name="planting_depth"
                                            label={
                                                t("seedingDepth", {
                                                    ns: "operations",
                                                }) +
                                                " (" +
                                                t("seedingDepthUnit", {
                                                    ns: "operations",
                                                }) +
                                                ")"
                                            }
                                            labelPlacement="outside"
                                            size="sm"
                                            placeholder=" "
                                            isReadOnly={
                                                singlePlantingOperationQuery.isLoading
                                            }
                                            isDisabled={
                                                singlePlantingOperationQuery.isLoading
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <div className="flex flex-row justify-between items-center w-full gap-2">
                                <div className="text-sm text-red-500">
                                    {editingExistingOperations &&
                                        "Any previously added planting operations will be updated"}
                                </div>
                                <div className="flex flex-row gap-2 items-center">
                                    <Button
                                        onPress={onFinish}
                                        size="sm"
                                        isDisabled={
                                            bulkUpsertPlantingOperation.isPending
                                        }
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        isLoading={
                                            bulkUpsertPlantingOperation.isPending
                                        }
                                        isDisabled={
                                            bulkUpsertPlantingOperation.isPending ||
                                            !isInitialized ||
                                            !selectedCrop
                                        }
                                        onPress={() => {
                                            f.trigger();
                                            f.handleSubmit(onSubmit)();
                                        }}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </div>
                        </ModalFooter>
                    </form>
                </FormProvider>
            </ModalContent>
        </Modal>
    );
}

export default BulkPlantingOperationForm;
